// useNotificationSocket — maintains a single websocket to /ws/notifications/ for
// live notification delivery. Mounted once (via NotificationHost) at the app
// shell, NOT per-component.
//
// Auth mirrors the app's other sockets (excel/pdf/rfp chat): the JWT is passed
// as a `?token=` query param, which the backend ZeroDBJWTAuthMiddleware validates.
//
// Resilience (handles "left the tab open for hours, notifications stop"):
//   1. Heartbeat: send an app-level ping every 25s and expect a pong. If no pong
//      for 60s the socket is a zombie (dead but not closed) — we drop it so it
//      reconnects. Detects sockets silently killed by sleep/idle/proxies.
//   2. Wake recovery: on tab-visible / online / window-focus we REST-resync
//      immediately (so missed notifications appear without a manual refresh) and
//      reconnect the socket if it isn't open.
//   3. Full resync on every (re)connect: refresh the unread count AND the list,
//      catching up anything missed while disconnected.
// Persistence is the backend source of truth, so a dropped socket never loses a
// notification — the resync always recovers it.
import { useEffect, useRef } from "react";
import toast from "react-hot-toast";
import Cookies from "js-cookie";
import { getFreshAccessToken } from "../pages/chat/api";
import useNotificationsStore from "../store/useNotificationsStore";

const WS_BASE = process.env.REACT_APP_WS_URL; // e.g. ws://localhost:8000
const MAX_BACKOFF_MS = 30000;
const HEARTBEAT_INTERVAL_MS = 25000; // send an app-level ping this often
const HEARTBEAT_TIMEOUT_MS = 60000; // no pong for this long => treat as dead
const RESYNC_THROTTLE_MS = 3000; // coalesce bursty wake events

export default function useNotificationSocket(identityKey = "") {
  const socketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const heartbeatTimerRef = useRef(null);
  const lastPongRef = useRef(0);
  const lastResyncRef = useRef(0);
  const attemptRef = useRef(0);
  const stoppedRef = useRef(false);

  // Re-runs whenever the identity (user + workspace) changes, so the socket
  // reconnects with the new user's token and joins their channel — and stays
  // off entirely when logged out.
  useEffect(() => {
    if (!identityKey) return undefined; // logged out: no socket, no listeners
    stoppedRef.current = false;

    const isOpen = () =>
      socketRef.current && socketRef.current.readyState === WebSocket.OPEN;

    const clearReconnect = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const stopHeartbeat = () => {
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
    };

    // Catch up from the server via REST. Safe to call anytime — it recovers the
    // UI even when the socket is dead. Throttled so bursty wake events (focus +
    // visibilitychange firing together) don't double-fetch.
    const resync = () => {
      const now = Date.now();
      if (now - lastResyncRef.current < RESYNC_THROTTLE_MS) return;
      lastResyncRef.current = now;
      const store = useNotificationsStore.getState();
      store.refreshUnreadCount();
      store.loadAll(true); // silent: no loading flash
    };

    const startHeartbeat = () => {
      stopHeartbeat();
      lastPongRef.current = Date.now();
      heartbeatTimerRef.current = setInterval(() => {
        if (!isOpen()) return;
        // No pong for too long => zombie socket; drop it so onclose reconnects.
        if (Date.now() - lastPongRef.current > HEARTBEAT_TIMEOUT_MS) {
          try {
            socketRef.current.close();
          } catch {
            /* ignore */
          }
          return;
        }
        try {
          socketRef.current.send(JSON.stringify({ action: "ping" }));
        } catch {
          /* ignore */
        }
      }, HEARTBEAT_INTERVAL_MS);
    };

    const scheduleReconnect = () => {
      if (stoppedRef.current) return;
      const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** attemptRef.current);
      attemptRef.current += 1;
      clearReconnect();
      reconnectTimerRef.current = setTimeout(connect, delay);
    };

    const connect = async () => {
      if (stoppedRef.current) return;
      if (!Cookies.get("access_token") || !WS_BASE) return;

      const token = await getFreshAccessToken();
      if (!token || stoppedRef.current) return;

      // Tear down any stale socket before opening a new one.
      try {
        if (socketRef.current) {
          socketRef.current.onclose = null;
          socketRef.current.close();
        }
      } catch {
        /* ignore */
      }

      const url = `${WS_BASE}/ws/notifications/?token=${encodeURIComponent(token)}`;
      let socket;
      try {
        socket = new WebSocket(url);
      } catch {
        scheduleReconnect();
        return;
      }
      socketRef.current = socket;

      socket.onopen = () => {
        attemptRef.current = 0; // reset backoff on success
        lastPongRef.current = Date.now();
        startHeartbeat();
        resync(); // catch up unread count + list on (re)connect
      };

      socket.onmessage = (event) => {
        let data;
        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }
        if (data.event === "pong") {
          lastPongRef.current = Date.now();
          return;
        }
        const store = useNotificationsStore.getState();
        if (data.event === "new_notification" && data.notification) {
          store.pushIncoming(data.notification);
          if (typeof data.unread_count === "number") {
            store.setUnreadCount(data.unread_count);
          }
          // Only raise a popup when the backend flags this notification as
          // toast-worthy (e.g. bid-deadline sends toast:false). Missing flag ==
          // legacy payload -> default to showing it.
          if (data.notification.body && data.toast !== false) {
            toast(data.notification.body, { icon: "🔔", duration: 4000 });
          }
        } else if (data.event === "toast" && data.notification) {
          // Transient popup only — never added to the bell list or unread badge.
          if (data.notification.body) {
            toast(data.notification.body, { icon: "🔔", duration: 4000 });
          }
        } else if (data.event === "unread_count") {
          store.setUnreadCount(data.unread_count);
        }
      };

      socket.onerror = () => {
        // onclose will follow and handle reconnect.
      };

      socket.onclose = () => {
        stopHeartbeat();
        if (stoppedRef.current) return;
        scheduleReconnect();
      };
    };

    // When the user returns (tab visible / network back / window focus), catch up
    // immediately and make sure the socket is alive. This is the key fix for
    // "left it open a long time, notifications don't arrive until refresh".
    const handleWake = () => {
      if (stoppedRef.current) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      resync(); // immediate catch-up regardless of socket health
      if (!isOpen()) {
        attemptRef.current = 0; // reconnect right away, no backoff
        clearReconnect();
        connect();
      } else {
        // Socket claims open — ping to confirm it is really alive.
        try {
          socketRef.current.send(JSON.stringify({ action: "ping" }));
        } catch {
          /* ignore */
        }
      }
    };

    connect();

    document.addEventListener("visibilitychange", handleWake);
    window.addEventListener("online", handleWake);
    window.addEventListener("focus", handleWake);

    return () => {
      stoppedRef.current = true;
      clearReconnect();
      stopHeartbeat();
      document.removeEventListener("visibilitychange", handleWake);
      window.removeEventListener("online", handleWake);
      window.removeEventListener("focus", handleWake);
      try {
        if (socketRef.current) {
          socketRef.current.onclose = null;
          socketRef.current.close();
          socketRef.current = null;
        }
      } catch {
        /* ignore */
      }
    };
  }, [identityKey]);
}
